import { ChartColumn, Expand } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { useChartTheme } from "@/shared/hooks/useChartTheme";

type AgePyramidPoint = {
  label: string;
  value: number;
};

type EnterpriseDashboardAgePyramidProps = {
  title: string;
  subtitle: string;
  viewAllLabel?: string;
  detailHref?: string;
  data: AgePyramidPoint[];
};

export function EnterpriseDashboardAgePyramid({
  title,
  subtitle,
  viewAllLabel,
  detailHref,
  data,
}: EnterpriseDashboardAgePyramidProps) {
  const { tick } = useChartTheme();

  const card = (
    <div
      className={`theme-card flex h-full flex-col p-5${detailHref ? " cursor-pointer transition-shadow hover:shadow-md" : ""}`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-soft text-primary">
            <ChartColumn size={20} strokeWidth={2} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <p className="mt-1 text-xs text-muted">{subtitle}</p>
          </div>
        </div>
        {viewAllLabel && detailHref ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
            {viewAllLabel}
            <Expand size={13} />
          </span>
        ) : null}
      </div>
      <div className="min-h-[260px] flex-1">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
          >
            <XAxis
              type="number"
              domain={[0, 40]}
              ticks={[0, 5, 10, 15, 20, 25, 30, 35, 40]}
              tickFormatter={(value) => `${value}%`}
              axisLine={false}
              tickLine={false}
              tick={{ fill: tick, fontSize: 11 }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={88}
              axisLine={false}
              tickLine={false}
              tick={{ fill: tick, fontSize: 11 }}
            />
            <Bar dataKey="value" fill="#c7d4ef" radius={[0, 8, 8, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  if (detailHref) {
    return (
      <Link to={detailHref} className="block h-full no-underline">
        {card}
      </Link>
    );
  }

  return card;
}
